import { useMutation, useQueryClient } from '@tanstack/react-query';
import { shiftsApi } from '@/features/shifts/api';
import { shiftsKeys } from '@/features/shifts/hooks';
import type { ShiftWithTotals } from '@/features/shifts/schemas';

interface OptimisticContext {
  previousDetail?: ShiftWithTotals;
  previousCounters?: Record<string, number>;
}

/**
 * Conta de itens por examTypeId, mantida em cache separado do detail para
 * suportar optimistic updates rápidos no QuickTapGrid — o detail carrega
 * totais numéricos agregados (server-side), e o counters cache traz a
 * decomposição por tipo que o grid renderiza ao vivo.
 */
export const itemCountersKey = (shiftId: string): [string, string, string] =>
  ['shifts', 'counters', shiftId];

export interface ItemCounters {
  [examTypeId: string]: number;
}

export function useShiftItemMutations(
  shiftId: string,
  getUnitValue: (examTypeId: string) => number,
): {
  registerItem: ReturnType<typeof useMutation<ShiftWithTotals, Error, { examTypeId: string }, OptimisticContext>>;
  undoLast: ReturnType<typeof useMutation<ShiftWithTotals, Error, { lastExamTypeId: string | null }, OptimisticContext>>;
  removeItem: ReturnType<typeof useMutation<ShiftWithTotals, Error, { examTypeId: string }, OptimisticContext>>;
} {
  const qc = useQueryClient();

  const bumpDetail = (delta: number, unit: number): void => {
    const key = shiftsKeys.detail(shiftId);
    const current = qc.getQueryData<ShiftWithTotals>(key);
    if (!current) return;
    const newItemsCount = current.totals.itemsCount + delta;
    const newItemsTotal = Number((current.totals.itemsTotal + delta * unit).toFixed(2));
    qc.setQueryData<ShiftWithTotals>(key, {
      ...current,
      totals: {
        ...current.totals,
        itemsCount: newItemsCount,
        itemsTotal: newItemsTotal,
        total: Number((current.totals.fixedValue + newItemsTotal).toFixed(2)),
      },
    });
  };

  const registerItem = useMutation<
    ShiftWithTotals,
    Error,
    { examTypeId: string },
    OptimisticContext
  >({
    mutationFn: ({ examTypeId }) => shiftsApi.registerItem(shiftId, examTypeId),
    onMutate: async ({ examTypeId }) => {
      await qc.cancelQueries({ queryKey: shiftsKeys.detail(shiftId) });
      await qc.cancelQueries({ queryKey: itemCountersKey(shiftId) });

      const previousDetail = qc.getQueryData<ShiftWithTotals>(shiftsKeys.detail(shiftId));
      const previousCounters =
        qc.getQueryData<ItemCounters>(itemCountersKey(shiftId)) ?? {};

      const nextCounters: ItemCounters = {
        ...previousCounters,
        [examTypeId]: (previousCounters[examTypeId] ?? 0) + 1,
      };
      qc.setQueryData<ItemCounters>(itemCountersKey(shiftId), nextCounters);

      bumpDetail(+1, getUnitValue(examTypeId));

      return { previousDetail, previousCounters };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      if (ctx.previousDetail)
        qc.setQueryData(shiftsKeys.detail(shiftId), ctx.previousDetail);
      if (ctx.previousCounters)
        qc.setQueryData(itemCountersKey(shiftId), ctx.previousCounters);
    },
    onSuccess: (data) => {
      qc.setQueryData(shiftsKeys.detail(shiftId), data);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: shiftsKeys.summary(shiftId) });
    },
  });

  const undoLast = useMutation<
    ShiftWithTotals,
    Error,
    { lastExamTypeId: string | null },
    OptimisticContext
  >({
    mutationFn: () => shiftsApi.undoLastItem(shiftId),
    onMutate: async ({ lastExamTypeId }) => {
      await qc.cancelQueries({ queryKey: shiftsKeys.detail(shiftId) });
      await qc.cancelQueries({ queryKey: itemCountersKey(shiftId) });

      const previousDetail = qc.getQueryData<ShiftWithTotals>(shiftsKeys.detail(shiftId));
      const previousCounters =
        qc.getQueryData<ItemCounters>(itemCountersKey(shiftId)) ?? {};

      if (lastExamTypeId && (previousCounters[lastExamTypeId] ?? 0) > 0) {
        const nextCounters: ItemCounters = {
          ...previousCounters,
          [lastExamTypeId]: previousCounters[lastExamTypeId] - 1,
        };
        qc.setQueryData<ItemCounters>(itemCountersKey(shiftId), nextCounters);
        bumpDetail(-1, getUnitValue(lastExamTypeId));
      }

      return { previousDetail, previousCounters };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      if (ctx.previousDetail)
        qc.setQueryData(shiftsKeys.detail(shiftId), ctx.previousDetail);
      if (ctx.previousCounters)
        qc.setQueryData(itemCountersKey(shiftId), ctx.previousCounters);
    },
    onSuccess: (data) => {
      qc.setQueryData(shiftsKeys.detail(shiftId), data);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: shiftsKeys.summary(shiftId) });
    },
  });

  const removeItem = useMutation<
    ShiftWithTotals,
    Error,
    { examTypeId: string },
    OptimisticContext
  >({
    mutationFn: ({ examTypeId }) => shiftsApi.removeItem(shiftId, examTypeId),
    onMutate: async ({ examTypeId }) => {
      await qc.cancelQueries({ queryKey: shiftsKeys.detail(shiftId) });
      await qc.cancelQueries({ queryKey: itemCountersKey(shiftId) });

      const previousDetail = qc.getQueryData<ShiftWithTotals>(shiftsKeys.detail(shiftId));
      const previousCounters =
        qc.getQueryData<ItemCounters>(itemCountersKey(shiftId)) ?? {};

      if ((previousCounters[examTypeId] ?? 0) > 0) {
        const nextCounters: ItemCounters = {
          ...previousCounters,
          [examTypeId]: previousCounters[examTypeId] - 1,
        };
        qc.setQueryData<ItemCounters>(itemCountersKey(shiftId), nextCounters);
        bumpDetail(-1, getUnitValue(examTypeId));
      }

      return { previousDetail, previousCounters };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      if (ctx.previousDetail)
        qc.setQueryData(shiftsKeys.detail(shiftId), ctx.previousDetail);
      if (ctx.previousCounters)
        qc.setQueryData(itemCountersKey(shiftId), ctx.previousCounters);
    },
    onSuccess: (data) => {
      qc.setQueryData(shiftsKeys.detail(shiftId), data);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: shiftsKeys.summary(shiftId) });
    },
  });

  return { registerItem, undoLast, removeItem };
}
